import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Store, 
  Menu, 
  Star,
  Shield,
  X,
  LogOut,
  Settings,
  Calendar,
  Bell,
  CreditCard,
  ShoppingBag,
  BarChart3,
  Lock,
  Crown,
  Globe,
  Monitor,
  MessageCircle
} from 'lucide-react';
import { cn } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { restaurantService } from '../../services/restaurantService';
import { toast } from 'react-toastify';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

// Mapping des icônes par nom
const iconMap: Record<string, React.ComponentType<any>> = {
  LayoutDashboard,
  Store,
  Menu,
  Star,
  Shield,
  Settings,
  Calendar,
  CreditCard,
  ShoppingBag,
  BarChart3,
  Bell,
  Globe,
  Monitor,
  MessageCircle
};

// Sections statiques - plus de configuration complexe
const STATIC_SECTIONS = [
  { id: 'dashboard', label: 'Dashboard', href: '/dashboard', icon: 'LayoutDashboard' },
  { id: 'restaurant', label: 'Fiche Établissement', href: '/restaurant', icon: 'Store' },
  { id: 'menu', label: 'Menu & QR Code', href: '/menu', icon: 'Menu' },
  { id: 'reviews', label: 'Avis Clients', href: '/reviews', icon: 'Star' },
  { id: 'orders', label: 'Commandes', href: '/orders', icon: 'ShoppingBag' },
  { id: 'reservations', label: 'Réservations', href: '/reservations', icon: 'Calendar' },
  { id: 'messaging', label: 'Messagerie', href: '/messaging', icon: 'MessageCircle' },
  { id: 'analytics', label: 'Analytics', href: '/analytics', icon: 'BarChart3' },
  { id: 'social', label: 'Réseaux Sociaux', href: '/social', icon: 'Globe' },

  { id: 'website', label: 'Site Web', href: '/website', icon: 'Monitor' },
  { id: 'settings', label: 'Paramètres', href: '/settings', icon: 'Settings' },
  { id: 'subscription', label: 'Mon Abonnement', href: '/subscription', icon: 'CreditCard' }
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, userProfile } = useAuth();
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [restaurantLogo, setRestaurantLogo] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState<string>('Mon Restaurant');

  useEffect(() => {
    loadRestaurantInfo();
  }, [user]);

  const loadRestaurantInfo = async () => {
    if (!user?.uid) return;
    
    try {
      const restaurant = await restaurantService.getRestaurant(user.uid);
      if (restaurant) {
        if (restaurant.logoUrl) {
          setRestaurantLogo(restaurant.logoUrl);
        }
        if (restaurant.name) {
          setRestaurantName(restaurant.name);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des informations du restaurant:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getIconComponent = (iconName?: string) => {
    if (!iconName) return Settings;
    return iconMap[iconName] || Settings;
  };

  // Sidebar toujours accessible avec toutes les sections
  return (
    <>
      {/* Overlay mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-900 bg-opacity-50 z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar normale */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64',
          'bg-gradient-to-b from-white/95 via-white/90 to-gray-50/95',
          'backdrop-blur-xl border-r border-gray-200/50',
          'shadow-2xl shadow-gray-900/10 transform transition-all duration-300 ease-in-out',
          'lg:translate-x-0 overflow-visible',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <div className="flex flex-col h-full overflow-visible">
          {/* En-tête avec logo centré */}
          <div className="p-6 border-b border-gray-200/30 bg-gradient-to-r from-white/80 to-gray-50/80 backdrop-blur-sm">
            <div className="flex items-center justify-end mb-4 lg:hidden">
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-gradient-to-r hover:from-[#FF7900]/10 hover:to-[#FF8A20]/10 text-[#FF7900] hover:text-[#FF7900] transition-all duration-200 backdrop-blur-sm border border-transparent hover:border-[#FF7900]/20 hover:shadow-md group"
              >
                <X className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
              </button>
            </div>
            
            {/* Logo du restaurant centré et agrandi */}
            {restaurantLogo && (
              <div className="flex justify-center mb-4">
                <NavLink 
                  to="/dashboard" 
                  className="cursor-pointer transition-transform hover:scale-110"
                >
                  <img 
                    src={restaurantLogo} 
                    alt="Logo du restaurant" 
                    className="h-12 w-auto object-contain"
                  />
                </NavLink>
              </div>
            )}
            
            {/* Informations du restaurant */}
            <div className="w-full flex items-center justify-center px-3 py-2 bg-gradient-to-r from-gray-50/80 to-white/80 backdrop-blur-sm rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-all duration-200">
              <span className="text-sm font-medium bg-gradient-to-r from-gray-700 to-gray-600 bg-clip-text text-transparent truncate">{restaurantName}</span>
            </div>
          </div>

          {/* Navigation principale */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto overflow-x-visible bg-gradient-to-b from-transparent to-gray-50/20">
            {STATIC_SECTIONS.map((section) => {
              const isActive = location.pathname === section.href;
              const IconComponent = getIconComponent(section.icon);
              
              return (
                <NavLink
                  key={section.id}
                  to={section.href}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 group',
                      'backdrop-blur-sm border border-transparent',
                      isActive
                        ? 'bg-gradient-to-r from-[#FF7900] to-[#FF8A20] text-white shadow-lg shadow-[#FF7900]/25 border-[#FF7900]/20 transform scale-[1.02]'
                        : 'text-gray-700 hover:bg-gradient-to-r hover:from-[#FF7900]/10 hover:to-[#FF8A20]/10 hover:text-[#FF7900] hover:border-[#FF7900]/20 hover:shadow-md hover:transform hover:scale-[1.01]'
                    )
                  }
                >
                  <IconComponent className="w-5 h-5 mr-3 transition-transform duration-200 group-hover:scale-110" />
                  {section.label}
                </NavLink>
              );
            })}
          </nav>

          {/* Bouton de déconnexion en bas */}
          <div className="p-6 border-t border-gray-200/30 bg-gradient-to-r from-white/80 to-gray-50/80 backdrop-blur-sm">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-4 py-3 text-gray-500 hover:text-gray-700 hover:bg-gradient-to-r hover:from-gray-100/80 hover:to-gray-50/80 rounded-xl transition-all duration-200 backdrop-blur-sm border border-transparent hover:border-gray-200/50 hover:shadow-md group"
            >
              <LogOut className="w-4 h-4 mr-2 transition-transform duration-200 group-hover:scale-110" />
              Déconnexion
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;